import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import FeaturesSection from "@/components/FeaturesSection";
import TechnologySection from "@/components/TechnologySection";
import CameraScanSection from "@/components/CameraScanSection";
import DoctorVerifiedSection from "@/components/DoctorVerifiedSection";
import HowItWorksSection from "@/components/HowItWorksSection";
import VisionSection from "@/components/VisionSection";
import CTASection from "@/components/CTASection";
import ConfidentialAccessSection from "@/components/ConfidentialAccessSection";
import Footer from "@/components/Footer";
import SplashScreen from "@/components/SplashScreen";
import { supabase } from "@/integrations/supabase/client";

const SPLASH_KEY = 'apd_splash_shown';
const JOURNEY_KEY = 'apd_patient_journey';

/** Check localStorage for an active (non-expired) patient journey */
function getActiveJourney(): { stage: string; chiefComplaint: string | null } | null {
  try {
    const raw = localStorage.getItem(JOURNEY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { stage?: string; chiefComplaint?: string | null; expiresAt?: number };
    if (!parsed.stage || parsed.stage === 'choose_start') return null;
    if (parsed.expiresAt && Date.now() > parsed.expiresAt) return null;
    return { stage: parsed.stage, chiefComplaint: parsed.chiefComplaint ?? null };
  } catch {
    return null;
  }
}

/**
 * Warm up every critical resource in parallel while the splash animation plays.
 * Returns a promise that resolves when everything is ready (or times out at 8s).
 */
function createWarmupPromise(): Promise<void> {
  const tasks: Promise<unknown>[] = [
    // 1. Restore auth session (critical — gates all protected routes)
    supabase.auth.getSession(),

    // 2. Wake up the Supabase DB connection (cold start = 2-5s, this pre-pays that cost)
    supabase.from('profiles').select('id').limit(1),

    // 3. Warm up user_roles table (used immediately after auth)
    supabase.from('user_roles').select('role').limit(1),

    // 4. Warm up consultations table (first thing dashboard loads)
    supabase.from('consultations').select('id').limit(1),

    // 5. Prefetch fonts — browser usually handles this, but explicitly touching
    //    document.fonts ensures they're loaded before the site paints
    typeof document !== 'undefined' && document.fonts
      ? document.fonts.ready
      : Promise.resolve(),
  ];

  // Never block longer than 8s regardless of network — splash is 6s so this is a safety net
  const hardTimeout = new Promise<void>((resolve) => setTimeout(resolve, 8000));

  return Promise.race([
    Promise.allSettled(tasks).then(() => {}),
    hardTimeout,
  ]);
}

const Index = () => {
  const navigate = useNavigate();
  const [showSplash, setShowSplash] = useState(() => !sessionStorage.getItem(SPLASH_KEY));
  const [activeJourney, setActiveJourney] = useState<{ stage: string; chiefComplaint: string | null } | null>(null);
  // Start warmup immediately on mount — runs in parallel with the animation
  const warmupRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (!sessionStorage.getItem(SPLASH_KEY)) {
      warmupRef.current = createWarmupPromise();
    }
    // Check for an active journey to show resume banner
    setActiveJourney(getActiveJourney());
  }, []);

  const handleSplashComplete = useCallback(async () => {
    // Wait for all warmup tasks before letting the splash disappear
    if (warmupRef.current) {
      await warmupRef.current;
    }
    sessionStorage.setItem(SPLASH_KEY, '1');
    setShowSplash(false);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
      <Navbar />
      {/* Resume journey banner — only shown if patient has an active session in localStorage */}
      {activeJourney && (
        <div className="bg-blue-950/60 border-b border-blue-800/40 py-2.5 px-4">
          <div className="container mx-auto flex items-center justify-between gap-3">
            <p className="text-sm text-blue-300">
              {activeJourney.chiefComplaint
                ? `Continue your consultation for "${activeJourney.chiefComplaint}"`
                : 'You have an active health session'}
            </p>
            <button
              onClick={() => navigate('/consultation')}
              className="text-xs font-semibold text-blue-200 bg-blue-700/40 hover:bg-blue-700/60 px-3 py-1 rounded-lg transition-colors shrink-0"
            >
              Resume →
            </button>
          </div>
        </div>
      )}
      <main>
        <HeroSection />
        <FeaturesSection />
        <TechnologySection />
        <CameraScanSection />
        <DoctorVerifiedSection />
        <HowItWorksSection />
        <VisionSection />
        <CTASection />
        <ConfidentialAccessSection />
      </main>
      <Footer />
    </div>
  );
};

export default Index;
